import './EmailSettingsSection.css';
import React, { useState, useEffect, useCallback } from 'react';
import { loadEmailConfig, saveEmailConfig, testSmtpConnection, detectEmailVars } from '../../logic/emailSettingsLogic';

export default function EmailSettingsSection({ notify, can }) {
  const [emailConfig, setEmailConfig] = useState({
    host: '', port: '', port_type: 'SSL', username: '', password: '',
    mail_sender_name: '', mail_sender_id: '', mail_reply_id: '',
    welcome_subject: '', welcome_email_format: ''
  });
  const [emailTestStatus, setEmailTestStatus] = useState('');
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  const [showEmailVariables, setShowEmailVariables] = useState(false);
  const [copyToast, setCopyToast] = useState(false);
  const [busy, setBusy] = useState({});
  const isBusy = key => !!busy[key];
  const withBusy = async (key, fn) => {
    setBusy(b => ({ ...b, [key]: true }));
    try { await fn(); } finally { setBusy(b => ({ ...b, [key]: false })); }
  };

  const load = useCallback(async () => {
    try {
      const config = await loadEmailConfig();
      if (config) setEmailConfig(config);
    } catch (e) { notify(e.response?.data?.message || 'Failed to load email config', true); }
  }, [notify]);

  useEffect(() => { load(); }, [load]);

  const detectEmailVarsFromForm = () => {
    return detectEmailVars(emailConfig.welcome_subject, emailConfig.welcome_email_format);
  };

  const copyEmailFormat = () => {
    navigator.clipboard.writeText(emailConfig.welcome_email_format);
    setCopyToast(true);
    setTimeout(() => setCopyToast(false), 2000);
  };

  const handleSaveEmailConfig = async e => {
    if (e && e.preventDefault) e.preventDefault();
    await withBusy('save-email', async () => {
      try {
        await saveEmailConfig(emailConfig);
        notify('Email config updated');
        document.querySelector('.admin-main').scrollTo({ top: 0, behavior: 'smooth' });
        load();
      } catch (err) {
        notify(err.response?.data?.message || 'Failed', true);
        document.querySelector('.admin-main').scrollTo({ top: 0, behavior: 'smooth' });
      }
    });
  };

  const handleTestEmailConfig = async () => {
    await withBusy('test-email', async () => {
      setEmailTestStatus('');
      try {
        await testSmtpConnection(emailConfig);
        setEmailTestStatus('🟢 Server connected successfully');
      } catch (err) {
        setEmailTestStatus(`🔴 ${err.response?.data?.message || 'Failed to connect'}`);
      }
    });
  };

  return (
    <div className="fade-up">
      <div className="form-section email-config-container" style={{width:'100%',marginBottom:'1.5rem', padding: 0}}>
        <div style={{padding: '1.5rem 1.75rem', borderBottom: '1px solid var(--border)', background: 'var(--bg3)'}}>
           <h3 style={{margin:0, borderBottom:'none', paddingBottom:0}}>Email Credentials</h3>
        </div>
        <div style={{padding: '1.75rem'}}>
          <form onSubmit={handleSaveEmailConfig}>
            <div className="form-grid" style={{marginBottom:'1rem', rowGap: '1.5rem', columnGap: '2rem'}}>
              <div className="input-group">
                <label>Host Name:</label>
                <input className="input-field" placeholder="email-smtp.us-east-1.amazonaws.com"
                  value={emailConfig.host} onChange={e=>setEmailConfig(f=>({...f,host:e.target.value}))} required disabled={!can('emailconfig','edit')}/>
              </div>
              <div className="input-group">
                <label>Port Name:</label>
                <input type="number" className="input-field" placeholder="465"
                  value={emailConfig.port} onChange={e=>setEmailConfig(f=>({...f,port:e.target.value}))} required disabled={!can('emailconfig','edit')}/>
              </div>
              <div className="input-group">
                <label>Port Type:</label>
                <select className="input-field" value={emailConfig.port_type} onChange={e=>setEmailConfig(f=>({...f,port_type:e.target.value}))} disabled={!can('emailconfig','edit')}>
                  {['SSL','TLS','None'].map(d=><option key={d}>{d}</option>)}
                </select>
              </div>
              <div className="input-group">
                <label>Username:</label>
                <input className="input-field" placeholder="Username"
                  value={emailConfig.username} onChange={e=>setEmailConfig(f=>({...f,username:e.target.value}))} required disabled={!can('emailconfig','edit')}/>
              </div>
              <div className="input-group">
                <label>Password:</label>
                <input type="password" className="input-field" placeholder="••••••••••••••••"
                  value={emailConfig.password} onChange={e=>setEmailConfig(f=>({...f,password:e.target.value}))} required disabled={!can('emailconfig','edit')}/>
              </div>
              <div className="input-group">
                <label>Mail Sender Name:</label>
                <input className="input-field" placeholder="AptitudeTest Pro"
                  value={emailConfig.mail_sender_name} onChange={e=>setEmailConfig(f=>({...f,mail_sender_name:e.target.value}))} required disabled={!can('emailconfig','edit')}/>
              </div>
              <div className="input-group">
                <label>Mail Sender Id:</label>
                <input type="email" className="input-field" placeholder="no-reply@domain.com"
                  value={emailConfig.mail_sender_id} onChange={e=>setEmailConfig(f=>({...f,mail_sender_id:e.target.value}))} required disabled={!can('emailconfig','edit')}/>
              </div>
              <div className="input-group">
                <label>Mail Reply Id:</label>
                <input type="email" className="input-field" placeholder="support@domain.com"
                  value={emailConfig.mail_reply_id} onChange={e=>setEmailConfig(f=>({...f,mail_reply_id:e.target.value}))} disabled={!can('emailconfig','edit')}/>
              </div>
            </div>
            {can('emailconfig','edit') && (
              <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:'1.5rem'}}>
                <div style={{display:'flex', alignItems:'center', gap: '0.5rem', color: emailTestStatus.includes('🔴') ? 'var(--red)' : emailTestStatus.includes('🟢') ? 'var(--green)' : 'var(--text)', fontSize: '0.85rem', fontWeight: '600'}}>
                  {emailTestStatus.includes('🟢') && <span style={{width:'8px',height:'8px',borderRadius:'50%',background:'var(--green)'}}></span>}
                  {emailTestStatus.includes('🔴') && <span style={{width:'8px',height:'8px',borderRadius:'50%',background:'var(--red)'}}></span>}
                  {emailTestStatus}
                </div>
                <button type="button" className="btn btn-primary" disabled={isBusy('test-email')} onClick={handleTestEmailConfig} style={{background: '#3b82f6', borderColor: '#3b82f6'}}>
                  {isBusy('test-email') ? <><span className="btn-spinner"/>Testing…</> : 'Test Connection'}
                </button>
              </div>
            )}
          </form>
        </div>
      </div>
      <div className="form-section email-config-container" style={{width:'100%', padding: '1.75rem'}}>
        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: '1.5rem'}}>
          <h3 style={{margin:0, borderBottom:'none', paddingBottom:0, fontSize:'1.25rem', color:'var(--text)'}}>User Welcome Email</h3>
          <div style={{display:'flex', gap:'1rem', color:'var(--muted)'}}>
            <button type="button" className="act-btn" title="Variables info" style={{border: 'none', background:'transparent', fontSize: '1.2rem', color:'var(--accent2)'}} onClick={()=>setShowEmailVariables(true)}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2a10 10 0 1010 10A10 10 0 0012 2zm1 15h-2v-6h2zm0-8h-2V7h2z"/></svg>
            </button>
            <div style={{position:'relative'}}>
              <button type="button" className="act-btn" title="Copy template" style={{border: 'none', background:'transparent', fontSize: '1.2rem'}} onClick={copyEmailFormat}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M16 1H4C2.9 1 2 1.9 2 3v14h2V3h12V1zm3 4H8C6.9 5 6 5.9 6 7v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/></svg>
              </button>
              {copyToast && <div className="copy-toast">Copied!</div>}
            </div>
            <button type="button" className="act-btn" title="Preview email" style={{border: 'none', background:'transparent', fontSize: '1.2rem', color:'var(--muted)'}} onClick={()=>setShowEmailPreview(true)}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/></svg>
            </button>
          </div>
        </div>
        <div className="form-grid" style={{marginBottom:'1.5rem'}}>
          <div className="input-group" style={{gridColumn:'1/-1'}}>
            <label>Subject</label>
            <input className="input-field" placeholder="Welcome User"
              value={emailConfig.welcome_subject} onChange={e=>setEmailConfig(f=>({...f,welcome_subject:e.target.value}))} disabled={!can('emailconfig','edit')}/>
          </div>
          <div className="input-group" style={{gridColumn:'1/-1'}}>
            <label>Mail Format:</label>
            <textarea className="input-field custom-scrollbar" rows="12" placeholder="<!DOCTYPE html><html>..."
              style={{fontFamily: 'monospace', fontSize: '0.82rem', whiteSpace: 'pre'}}
              value={emailConfig.welcome_email_format} onChange={e=>setEmailConfig(f=>({...f,welcome_email_format:e.target.value}))} disabled={!can('emailconfig','edit')}/>
          </div>
        </div>
        {can('emailconfig','edit') && (
          <div className="form-actions" style={{justifyContent: 'flex-end', paddingTop: '1rem', borderTop: '1px solid var(--border)'}}>
            <button type="button" className="btn btn-outline" onClick={load} style={{borderColor: 'transparent', background: 'transparent'}}>Cancel</button>
            <button type="button" className="btn btn-primary" disabled={isBusy('save-email')} onClick={handleSaveEmailConfig} style={{background: '#3b82f6', borderColor: '#3b82f6'}}>
              {isBusy('save-email') ? <><span className="btn-spinner"/>Saving…</> : 'Update Settings'}
            </button>
          </div>
        )}
      </div>

      {/* ════════ EMAIL VARIABLES MODAL ════════ */}
        {showEmailVariables && (() => {
        const detected = detectEmailVarsFromForm();
        return (
          <div className="ev-overlay" onClick={()=>setShowEmailVariables(false)}>
            <div className="ev-modal" onClick={e=>e.stopPropagation()}>
              <div className="ev-modal-header">
                <div className="ev-modal-title">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" style={{color:'var(--accent2)'}}><path d="M12 2a10 10 0 1010 10A10 10 0 0012 2zm1 15h-2v-6h2zm0-8h-2V7h2z"/></svg>
                  Detected Template Variables
                </div>
                <button className="ev-close-btn" onClick={()=>setShowEmailVariables(false)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>
              <p className="ev-modal-desc">
                {detected.length > 0
                  ? 'These placeholders were auto-detected in your email subject & body.'
                  : 'No variables detected. Use placeholders like {{fullName}} in your email format.'}
              </p>
              {detected.length > 0 ? (
                <div className="ev-vars-list">
                  {detected.map(v=>(
                    <div key={v.tag} className="ev-var-row">
                      <code className="ev-var-tag">{v.tag}</code>
                      <div className="ev-var-info">
                        <span className="ev-var-desc">{v.desc}</span>
                        <span className="ev-var-example">e.g. <em>{v.example}</em></span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="ev-no-vars">
                  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{color:'var(--muted)',marginBottom:'0.5rem'}}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                  <span>No <code>{'{{variables}}'}</code> found</span>
                </div>
              )}
              <div className="ev-modal-footer">
                <button className="btn btn-primary btn-sm" onClick={()=>setShowEmailVariables(false)}>Got it</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ════════ EMAIL PREVIEW MODAL ════════ */}
      {showEmailPreview && (
        <div className="ev-overlay" onClick={()=>setShowEmailPreview(false)}>
          <div className="ep-modal" onClick={e=>e.stopPropagation()}>
            <div className="ev-modal-header">
              <div className="ev-modal-title">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" style={{color:'var(--accent2)'}}><path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/></svg>
                Email Preview
                {emailConfig.welcome_subject && (
                  <span style={{fontSize:'0.78rem',fontWeight:400,color:'var(--muted)',marginLeft:'0.5rem'}}>— {emailConfig.welcome_subject}</span>
                )}
              </div>
              <button className="ev-close-btn" onClick={()=>setShowEmailPreview(false)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="ep-modal-body">
              <iframe
                title="Email Preview"
                srcDoc={emailConfig.welcome_email_format || '<div style="font-family:sans-serif;padding:3rem;text-align:center;color:#999;">No email template defined yet.</div>'}
                style={{width:'100%', height:'100%', border:'none', display:'block'}}
                sandbox="allow-scripts allow-same-origin allow-popups"
              />
            </div>
            <div className="ev-modal-footer" style={{justifyContent:'flex-end'}}>
              <button className="btn btn-outline btn-sm" onClick={()=>setShowEmailPreview(false)}>Close Preview</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
